import React, { useMemo, useRef, useState } from "react";
import { FiTrash2, FiTruck, FiRotateCcw, FiChevronDown, FiChevronUp, FiPlus, FiX } from "react-icons/fi";
import { toast } from "react-toastify";
import SrMultiSelectDropdown from "./SrMultiSelectDropdown";
import { getSrCountLimitError } from "../utils/challanSrValidation";
import "../style/ProductRow.css";


function ProductSearchField({
  value,
  options,
  isOpen,
  onOpen,
  onSearch,
  onSelect,
  placeholder = "Search product...",
}) {
  const anchorRef = useRef(null);
  const inputRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState(280);

  const visibleOptions = useMemo(() => options || [], [options]);

  const handleSearch = (searchTerm) => {
    onSearch(searchTerm);
    onOpen(true);

    // Calculate dropdown position and max height
    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 280;

      let maxHeight = dropdownHeight;
      let position = 'bottom';

      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        position = 'top';
        maxHeight = Math.min(dropdownHeight, spaceAbove - 20);
      } else {
        position = 'bottom';
        maxHeight = Math.min(dropdownHeight, spaceBelow - 20);
      }

      setDropdownPosition(position);
      setDropdownMaxHeight(maxHeight);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      onOpen(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div className="erp-product-field erp-product-name-field" ref={anchorRef}>
      <label>Product Name</label>
      <div className="erp-product-search-wrapper">
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={value || ""}
          onChange={(event) => handleSearch(event.target.value)}
          onFocus={() => onOpen(true)}
          onBlur={() => setTimeout(() => onOpen(false), 200)}
          onKeyDown={handleKeyDown}
        />
        <div className="erp-product-search-chevron">
          {isOpen ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
        </div>
        {isOpen && (
          <div 
            className="erp-product-dropdown"
            style={{
              top: dropdownPosition === 'top' ? 'auto' : 'calc(100% + 6px)',
              bottom: dropdownPosition === 'top' ? 'calc(100% + 6px)' : 'auto',
              maxHeight: `${dropdownMaxHeight}px`
            }}
          >
            {visibleOptions.length === 0 ? (
              <div className="erp-product-empty">
                <strong>No Products Found</strong>
                <span>Try another product name.</span>
              </div>
            ) : (
              visibleOptions.map((item) => (
                <div
                  key={item.id || item.name}
                  className="erp-product-option"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onSelect(item);
                  }}
                >
                  <span className="erp-product-option-main">
                    <span className="erp-product-option-name">{item.name}</span>
                    <span className="erp-product-option-meta">
                      Available: {item.availableQty ?? item.availableQuantity ?? item.qty ?? item.quantity ?? "-"}
                    </span>
                  </span>
                  <span className="erp-product-option-type">{item.type || "Cylinder"}</span>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Toggle button + inline input that lets the user manually add one or
 * more SR numbers that aren't in the dropdown's option list. Multiple SR
 * numbers can be entered in a single line separated by "/" (e.g.
 * "1234/654/789") — each one is split out and added as its own entry.
 * Renders directly under an SrMultiSelectDropdown.
 */
function ManualSrEntry({ label, disabled, onAdd }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const inputRef = useRef(null);

  const handleToggle = () => {
    if (disabled) return;
    setOpen((prev) => {
      const next = !prev;
      if (next) {
        // focus after the input mounts
        setTimeout(() => inputRef.current?.focus(), 0);
      } else {
        setValue("");
      }
      return next;
    });
  };

  const handleSubmit = () => {
    // Split on "/" so a single entry like "1234/654" becomes two
    // separate SR numbers: "1234" and "654".
    const srNumbers = value
      .split("/")
      .map((part) => part.trim())
      .filter(Boolean);

    if (srNumbers.length === 0) return;

    onAdd(srNumbers);
    setValue("");
    inputRef.current?.focus();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSubmit();
    } else if (event.key === "Escape") {
      setOpen(false);
      setValue("");
    }
  };

  return (
    <div className="erp-manual-sr">
      <button
        type="button"
        className={`erp-manual-sr-toggle ${open ? "is-open" : ""}`}
        onClick={handleToggle}
        disabled={disabled}
      >
        {open ? <FiX size={13} /> : <FiPlus size={13} />}
        <span>{open ? "Cancel manual entry" : `Add manual ${label}`}</span>
      </button>

      {open && (
        <div className="erp-manual-sr-row">
          <input
            ref={inputRef}
            type="text"
            className="erp-manual-sr-input"
            placeholder={`e.g. 1234/654 for multiple ${label}s`}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button
            type="button"
            className="erp-manual-sr-add-btn"
            onClick={handleSubmit}
            disabled={!value.trim()}
          >
            Add
          </button>
        </div>
      )}
    </div>
  );
}


function srOptions(cylinders = []) {
  return cylinders.map((cylinder) => ({
    srNo: cylinder.srNo || cylinder.srno,
    batchNo: cylinder.batchNo,
    cylinderCapacity: cylinder.cylinderCapacity,
  }));
}

const ProductRow = React.memo(function ProductRow({
  product,
  index,
  productsCount,
  mode = "delivered",
  productSearchTerm,
  filteredGoods,
  goods,
  productDropdownOpen,
  onProductSearch,
  onProductDropdownOpen,
  onProductSelect,
  onUpdate,
  onRemove,
  availableDeliveredSrs = [],
  availableReceivedSrs = [],
}) {
  const isDelivered = mode === "delivered";
  const isReceived = mode === "received";
  const isCombined = mode === "combined";
  const productOptions = filteredGoods || goods || [];
  const canDelete = productsCount > 1;

  const deliveredSrList = isCombined
    ? product.deliveredSrNos || []
    : isDelivered
      ? product.selectedSrNos || []
      : [];
  const receivedSrList = isCombined
    ? product.receivedSrNos || []
    : isReceived
      ? product.selectedSrNos || []
      : [];

  const updateSrSelection = (field, updated, qty, label) => {
    const error = getSrCountLimitError({
      count: Array.isArray(updated) ? updated.length : 0,
      qty,
      label,
      productName: product.name,
    });

    if (error) {
      toast.error(error);
      return;
    }

    onUpdate(index, field, updated);
  };

  /**
   * Adds one or more manually-entered SR numbers to the given field's
   * selection list. Duplicates (already selected, or repeated within the
   * same manual entry) are skipped with a toast; each valid SR number is
   * added as its own, individually-tracked entry — so "1234/654" results
   * in two distinct SR selections, never one combined string.
   */
  const handleManualSrAdd = (field, currentList, qty, label, srNumbers) => {
    const list = Array.isArray(srNumbers) ? srNumbers : [srNumbers];
    const next = [...currentList];
    const added = [];
    const duplicates = [];

    list.forEach((srNo) => {
      if (!srNo) return;
      if (next.includes(srNo) || added.includes(srNo)) {
        duplicates.push(srNo);
        return;
      }
      next.push(srNo);
      added.push(srNo);
    });

    if (duplicates.length > 0) {
      toast.error(
        duplicates.length === 1
          ? `${duplicates[0]} is already added.`
          : `${duplicates.join(", ")} are already added.`
      );
    }

    if (added.length === 0) return;

    updateSrSelection(field, next, qty, label);
  };

  // In Delivered/Received-only mode, Type sits right after Product Name.
  // In Combined mode, Type keeps its original position (after both qty
  // fields) — layout there is unchanged.
  const typeField = (
    <div className="erp-product-field">
      <label>Type</label>
      <input
        type="text"
        placeholder="Type"
        value={product.type || ""}
        onChange={(event) => onUpdate(index, "type", event.target.value)}
      />
    </div>
  );

  return (
    <div className="erp-product-row">
      <div className="erp-product-main-row">
        <div className={`erp-product-primary-grid ${isCombined ? 'six-columns' : (isDelivered || isReceived) ? 'four-columns-with-sr' : 'three-columns'}`}>
          <ProductSearchField
            value={productSearchTerm || product.name || ""}
            options={productOptions}
            isOpen={!!productDropdownOpen}
            onOpen={(open) => onProductDropdownOpen(index, open)}
            onSearch={(term) => onProductSearch(index, term)}
            onSelect={(item) => onProductSelect(index, item)}
          />

          {!isCombined && typeField}

          {isReceived !== true && (
            <div className="erp-product-field">
              <label>
                <FiTruck size={12} /> Delivered Qty
              </label>
              <input
                type="text"
                placeholder="0"
                value={product.deliveredQty ?? ""}
                onChange={(event) => onUpdate(index, "deliveredQty", event.target.value)}
                readOnly={isReceived}
                disabled={isReceived}
              />
            </div>
          )}

          {isDelivered !== true && (
            <div className="erp-product-field">
              <label>
                <FiRotateCcw size={12} /> Received Qty
              </label>
              <input
                type="text"
                placeholder="0"
                value={product.receivedQty ?? ""}
                onChange={(event) => onUpdate(index, "receivedQty", event.target.value)}
                readOnly={isDelivered}
                disabled={isDelivered}
              />
            </div>
          )}
          {isCombined && typeField}

          {isCombined && (
            <div className="erp-product-field">
              <SrMultiSelectDropdown
                label="Delivered SR Numbers"
                options={srOptions(availableDeliveredSrs)}
                selected={deliveredSrList}
                onChange={(updated) =>
                  updateSrSelection(
                    "deliveredSrNos",
                    updated,
                    product.deliveredQty,
                    "Delivered"
                  )
                }
                placeholder="Search delivered SR..."
                disabled={!product.name}
              />
              <ManualSrEntry
                label="delivered SR"
                disabled={!product.name}
                onAdd={(srNumbers) =>
                  handleManualSrAdd(
                    "deliveredSrNos",
                    deliveredSrList,
                    product.deliveredQty,
                    "Delivered",
                    srNumbers
                  )
                }
              />
            </div>
          )}

          {isCombined && (
            <div className="erp-product-field">
              <SrMultiSelectDropdown
                label="Received SR Numbers"
                options={srOptions(availableReceivedSrs)}
                selected={receivedSrList}
                onChange={(updated) =>
                  updateSrSelection(
                    "receivedSrNos",
                    updated,
                    product.receivedQty,
                    "Received"
                  )
                }
                placeholder="Search received SR..."
                disabled={!product.name}
              />
              <ManualSrEntry
                label="received SR"
                disabled={!product.name}
                onAdd={(srNumbers) =>
                  handleManualSrAdd(
                    "receivedSrNos",
                    receivedSrList,
                    product.receivedQty,
                    "Received",
                    srNumbers
                  )
                }
              />
            </div>
          )}

          {(isDelivered || isReceived) && !isCombined && (
            <div className="erp-product-field">
              <SrMultiSelectDropdown
                label={isDelivered ? "Delivered SR Numbers" : "Received SR Numbers"}
                options={isDelivered ? srOptions(product.cylinderData || []) : srOptions(product.cylinderData || [])}
                selected={isDelivered ? deliveredSrList : receivedSrList}
                onChange={(updated) =>
                  updateSrSelection(
                    "selectedSrNos",
                    updated,
                    isDelivered ? product.deliveredQty : product.receivedQty,
                    isDelivered ? "Delivered" : "Received"
                  )
                }
                placeholder={`Search ${isDelivered ? "delivered" : "received"} SR...`}
                disabled={!product.name}
              />
              <ManualSrEntry
                label={isDelivered ? "delivered SR" : "received SR"}
                disabled={!product.name}
                onAdd={(srNumbers) =>
                  handleManualSrAdd(
                    "selectedSrNos",
                    isDelivered ? deliveredSrList : receivedSrList,
                    isDelivered ? product.deliveredQty : product.receivedQty,
                    isDelivered ? "Delivered" : "Received",
                    srNumbers
                  )
                }
              />
            </div>
          )}
        </div>

        <button
          type="button"
          className="erp-product-delete"
          onClick={() => canDelete && onRemove(index)}
          disabled={!canDelete}
          title="Delete Product"
          aria-label="Delete Product"
        >
          <FiTrash2 size={16} />
        </button>
      </div>
    </div>
  );
});

export default ProductRow;